import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }
function apiFetch(path) {
  return fetch(`${API_BASE}${path}`, { headers: { 'x-worker-token': getToken() } }).then(r => r.ok ? r.json() : Promise.reject());
}

// Each card opens its own full page. Built as a hub so config sections (company
// info, default rates, permissions…) can be added alongside these databases.
const SECTIONS = [
  // One place for everything Titan charges. So far: Common Items (drilling, loops, pole
  // hold, mobilization…). The Rate Sheet tile stays until its rates move in as the Labor tab.
  { key: 'pricebook', label: 'Price Book',        path: '/admin/price-book', icon: '📒', color: '#1e3a5f', desc: 'Common items — drilling, loops, pole hold, mobilization', countPath: '/standard-items' },
  { key: 'materials', label: 'Material Database', path: '/admin/materials', icon: '📦', color: '#065f46', desc: 'Parts catalog — costs, labor & cut sheets', countPath: '/parts' },
  // Stock Items moved out (Pat, Sep 2026): it is "Stock Items Recap", the first tile in
  // Shop → Material → Stock Material. /admin/stock still opens it.
  { key: 'equipment', label: 'Equipment',        path: '/admin/equipment', icon: '🚜', color: '#0e7490', desc: 'Owned assets, assignments & service',      countPath: '/equipment' },
  { key: 'categories', label: 'Categories',      path: '/admin/categories', icon: '🏷️', color: '#065f46', desc: 'Material & Submittal categories, and the Vertical and Directional drill lists',        countPath: '/categories' },
  { key: 'checklists', label: 'Checklists & Load-up Lists', path: '/admin/checklists', icon: '✅', color: '#0e7490', desc: 'Checklists the field AI hands out; load-up lists for shop orders',  countPath: '/checklists' },
  { key: 'ratesheet',  label: 'Rate Sheet',      path: '/admin/rate-sheet', icon: '📄', color: '#7c3aed', desc: 'Labor/Truck/Equipment rates + customer PDF', countPath: '/rate-sheet' },
  // The lines that make up ONE foundation, signal pole, drilled foot… dropped onto a bid line from its blue + (Ryan, Oct 22 2026).
  { key: 'assemblies', label: 'Bid Assemblies', path: '/admin/bid-assemblies', icon: '🧩', color: '#4f46e5', desc: 'Foundations, signal poles, drilling — the sub-lines a bid line fills in', countPath: '/bid-assemblies' },
  // Production (trench size, feet a day, equipment + labor per day) and Material (pits, dumps, trucks) — the bid line's Trenching window works from it (Ryan, Oct 23 2026).
  { key: 'trenching', label: 'Trenching', path: '/admin/trenching', icon: '⛏️', color: '#92400e', desc: 'Production setups, pits & dump sites, trucks — trenching estimates on a bid line' },
];

function sessionRole() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.user?.role || JSON.parse(localStorage.getItem('titan_session') || 'null')?.role; } catch { return null; } }
const longDate = s => s ? new Date(s).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

// ── Connections: the outside services the app signs in to ─────────────────────
// QuickBooks Time runs the time clock. Until it is connected here the app holds a
// fixed token that someone has to replace by hand when it dies; connecting once gives
// the app a login that renews itself. Email shows which mailbox the app sends as.
function ConnectionsPanel() {
  const canConnect = ['Executive', 'Admin', 'admin'].includes(sessionRole());
  const [ts, setTs] = useState(null);
  const [mail, setMail] = useState(null);
  const [redirect, setRedirect] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const load = () => {
    apiFetch('/admin/tsheets-token').then(setTs).catch(() => setTs({ configured: false }));
    apiFetch('/mail/status').then(setMail).catch(() => setMail({ configured: false }));
  };
  useEffect(load, []);
  // Coming back from QuickBooks Time (the callback tab says "connected") — refresh
  // the card when this tab gets focus again.
  useEffect(() => { const f = () => load(); window.addEventListener('focus', f); return () => window.removeEventListener('focus', f); }, []);

  async function connect() {
    setBusy('connect'); setMsg('');
    try {
      const r = await fetch(`${API_BASE}/admin/tsheets-token/authorize-url`, { headers: { 'x-worker-token': getToken() } }).then(async x => { const j = await x.json(); if (!x.ok) throw new Error(j.error || 'Could not start'); return j; });
      setRedirect(r.redirect_uri || '');
      window.open(r.url, '_blank');
      setMsg('QuickBooks Time opened in a new tab — sign in and approve. This card updates when you come back.');
    } catch (e) { setMsg(e.message || 'Could not start the connection.'); }
    setBusy('');
  }
  async function renewNow() {
    setBusy('renew'); setMsg('');
    try {
      const r = await fetch(`${API_BASE}/admin/tsheets-token/renew`, { method: 'POST', headers: { 'x-worker-token': getToken() } }).then(async x => { const j = await x.json(); if (!x.ok) throw new Error(j.error || 'Renew failed'); return j; });
      setTs(r.status); setMsg(r.ok ? 'Renewed — the token is fresh.' : 'QuickBooks Time refused the renewal; see the office alert.');
    } catch (e) { setMsg(e.message); }
    setBusy('');
  }

  const card = { background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: 20, display: 'flex', flexDirection: 'column', gap: 8 };
  const pill = (text, tint) => <span style={{ fontSize: 11, fontWeight: 800, padding: '2px 9px', borderRadius: 20, background: tint + '18', color: tint, whiteSpace: 'nowrap' }}>{text}</span>;
  const btn = (primary) => ({ padding: '9px 16px', borderRadius: 9, border: primary ? 'none' : '1px solid var(--border)', background: primary ? '#1e3a5f' : '#fff', color: primary ? '#fff' : '#374151', fontSize: 13, fontWeight: 800, cursor: 'pointer' });
  const selfRenewing = ts?.source === 'stored' && ts?.can_auto_renew;

  return (
    <div style={{ marginTop: 28 }}>
      <h2 style={{ fontSize: 15, fontWeight: 800, color: '#111827', margin: '0 0 4px' }}>Connections</h2>
      <p style={{ fontSize: 12.5, color: '#6b7280', margin: '0 0 12px' }}>Outside services the app signs in to on the company's behalf.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
        <div style={{ ...card, borderTop: `4px solid ${selfRenewing ? '#15803d' : ts?.configured ? '#b45309' : '#b91c1c'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 26 }}>⏱️</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>QuickBooks Time</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Time clock, schedule, who's on the clock</div>
            </div>
            {ts === null ? pill('checking…', '#6b7280')
              : selfRenewing ? pill('Connected · renews itself', '#15803d')
              : ts?.configured ? pill('Fixed token · replaced by hand', '#b45309')
              : pill('Not connected', '#b91c1c')}
          </div>
          <div style={{ fontSize: 12.5, color: '#374151', lineHeight: 1.5 }}>
            {ts === null ? 'Checking…'
              : selfRenewing ? <>Signed in and renewing on its own. This token is good until <strong>{longDate(ts.expires_at)}</strong>{ts.renewed_at ? `, last renewed ${longDate(ts.renewed_at)}` : ''}; the app replaces it before then without anyone doing anything.</>
              : ts?.configured ? <>The app is using a fixed access token. It works until QuickBooks Time expires it, and then someone has to make a new one and install it. <strong>Connect</strong> once and the app gets a login that renews itself.</>
              : <>No QuickBooks Time token is installed. Clock in/out and the schedule are down until one is.</>}
          </div>
          {canConnect && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
              <button onClick={connect} disabled={!!busy} style={btn(!selfRenewing)}>{busy === 'connect' ? 'Opening…' : selfRenewing ? 'Reconnect' : 'Connect QuickBooks Time'}</button>
              {selfRenewing && <button onClick={renewNow} disabled={!!busy} style={btn(false)}>{busy === 'renew' ? 'Renewing…' : 'Renew now'}</button>}
            </div>
          )}
          {msg && <div style={{ fontSize: 12.5, color: '#1e3a5f', fontWeight: 600 }}>{msg}</div>}
          {redirect && (
            <div style={{ fontSize: 11.5, color: '#6b7280', lineHeight: 1.5 }}>
              If QuickBooks Time complains about the redirect, this address must be listed as the redirect URI on the app in QuickBooks Time's API add-on:<br />
              <code style={{ fontSize: 11, wordBreak: 'break-all' }}>{redirect}</code>
            </div>
          )}
        </div>

        <div style={{ ...card, borderTop: `4px solid ${mail?.configured ? '#15803d' : '#b91c1c'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 26 }}>✉️</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>Email</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>Invoices (quotes and submittals next)</div>
            </div>
            {mail === null ? pill('checking…', '#6b7280') : mail.configured ? pill('Sending', '#15803d') : pill('Not set up', '#b91c1c')}
          </div>
          <div style={{ fontSize: 12.5, color: '#374151', lineHeight: 1.5 }}>
            {mail === null ? 'Checking…' : mail.configured ? <>Sends as <strong>{mail.from}</strong> through the company mail server.</> : 'The server has no mail settings yet — the office adds them and this goes live.'}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({});

  useEffect(() => {
    SECTIONS.forEach(s => {
      if (!s.countPath) return;   // a tile with nothing to count (Trenching)
      apiFetch(s.countPath)
        .then(d => setCounts(c => ({ ...c, [s.key]: Array.isArray(d) ? d.length : null })))
        .catch(() => {});
    });
  }, []);

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Settings</h1>
          <p className="admin-page-sub">Company databases &amp; configuration</p>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
        {SECTIONS.map(s => (
          <button key={s.key} onClick={() => navigate(s.path)}
            style={{ textAlign: 'left', background: '#fff', border: '1px solid var(--border)', borderTop: `4px solid ${s.color}`, borderRadius: 12, padding: 20, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6, transition: 'box-shadow .15s, transform .1s' }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
            <div style={{ fontSize: 30 }}>{s.icon}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{s.label}</div>
            <div style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.4 }}>{s.desc}</div>
            {counts[s.key] != null && (
              <div style={{ fontSize: 12, color: s.color, fontWeight: 700, marginTop: 2 }}>{counts[s.key]} {counts[s.key] === 1 ? 'record' : 'records'}</div>
            )}
          </button>
        ))}
      </div>
      <ConnectionsPanel />
    </AdminLayout>
  );
}
